/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { DefaultAngularLand } from 'src/app/common/fancy-firebase/util/default-angular-land.service';
import {
	InvitedUserListFactory,
	ProjectMemberListFactory,
} from 'src/app/common/fancy-firebase/services/factories';
import { ProjectMembersApi } from 'src/app/app-data/state/ProjectMembersApi';
import { RolesApi } from 'src/app/app-data/state/RolesApi';
import { InvitedUsersApi } from 'src/app/app-data/state/InvitedUsersApi';
import { FancyFragmenter, FancyFragmenterFactory } from 'src/app/common/fancy-firebase/base/FancyFragmenter';
import { FancyAction } from 'src/app/common/fancy-firebase/base/FancyAction';
import { FancyFirebaseList } from 'src/app/common/fancy-firebase/base/FancyFirebaseList';
import { FancyIdJoin } from 'src/app/common/fancy-firebase/base/FancyIdJoin';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { AngularLandConnection } from 'src/app/common/fancy-firebase/base/interfaces';

import firebase from 'firebase/compat/app';

type ThingToDestroy = FancyIdJoin<any, any> | FancyFragmenter | FancyFirebaseList<any, any>;

@Injectable({
	providedIn: 'root',
})
export class FancyFirebaseProjectMembersApiBuilder {
	constructor(
		private defaultAngularLand: DefaultAngularLand,
		private fancyFragmenterFactory: FancyFragmenterFactory,
		private projectMemberListFactory: ProjectMemberListFactory,
		private invitedUsersListFactory: InvitedUserListFactory
	) {}

	public static createProjectMembersList(
		angularLand: AngularLandConnection,
		fancyFragmenterFactory: FancyFragmenterFactory,
		projectMemberListFactory: ProjectMemberListFactory,
		stuffToDestroy: ThingToDestroy[],
		projectId: string,
		rolesApi?: RolesApi
	) {
		const usersPath = fancyFirebaseGetters.getUsersPath();
		const userListFragment = fancyFragmenterFactory.create(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(usersPath)
		);
		const userList = new FancyFirebaseList(angularLand, userListFragment.emitter$, FancyAction);
		userListFragment.start();
		const usersJoiner = new FancyIdJoin('userId', userList, userListFragment);

		let pmRolesJoiner = null;
		if (rolesApi) {
			//share the same instance of this role list
			pmRolesJoiner = new FancyIdJoin('roleId', rolesApi.data);
			stuffToDestroy.push(pmRolesJoiner);
		}

		const projectMemberListPath = fancyFirebaseGetters.getProjectMembersPath(projectId);
		const projectMemberListRef = fancyFirebaseReferencer
			.getLegacyRef<firebase.database.Reference>(projectMemberListPath, projectId)
			.orderByChild('isApi')
			.startAt(null)
			.endAt(false); // TP-3572: Only include non-API members

		stuffToDestroy.push(usersJoiner);
		stuffToDestroy.push(userListFragment);
		stuffToDestroy.push(userList);

		return projectMemberListFactory.create(
			projectMemberListRef,
			pmRolesJoiner ? [pmRolesJoiner, usersJoiner] : [usersJoiner]
		);
	}

	public getProjectMembersApi(projectId: string, rolesApi: RolesApi) {
		const stuffToDestroy: ThingToDestroy[] = [];
		const projectMemberList = FancyFirebaseProjectMembersApiBuilder.createProjectMembersList(
			this.defaultAngularLand,
			this.fancyFragmenterFactory,
			this.projectMemberListFactory,
			stuffToDestroy,
			projectId,
			rolesApi
		);

		const projectMembersApi = new ProjectMembersApi(projectMemberList);

		//...christ
		for (const thing of stuffToDestroy) {
			projectMembersApi.addDestroyer(() => thing.destroy());
		}

		return projectMembersApi;
	}

	public getInvitedUsersApi(projectId: string, rolesApi: RolesApi) {
		//share the same instance of this role list
		const pmRolesJoiner = new FancyIdJoin('roleId', rolesApi.data);

		const invitedUsersListPath = fancyFirebaseGetters.getInvitedUsersProjectsPath(projectId);
		const invitedUsersList = this.invitedUsersListFactory.create(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(invitedUsersListPath, projectId),
			[pmRolesJoiner]
		);

		const invitedUsersApi = new InvitedUsersApi(invitedUsersList);
		invitedUsersApi.addDestroyer(() => pmRolesJoiner.destroy());
		return invitedUsersApi;
	}
}
